<template>
	<tw-modal
		title="Subscribe to Our Newsletter"
		modal-max-width="max-w-md"
		ref="modal">
		<div
			v-if="submitted"
			class="py-8 text-center">
			<CheckCircleIcon class="mx-auto h-10 w-10 text-green-500" />
			<p class="mt-3 text-sm text-gray-600">Thanks for subscribing!</p>
		</div>
		<form
			v-else
			class="py-4 space-y-4"
			@submit="onSubmit">
			<div>
				<label
					for="newsletter-email"
					class="block text-sm font-medium text-gray-700">Email Address <span class="text-red-500">*</span></label>
				<input
					id="newsletter-email"
					v-model="email"
					type="email"
					required
					class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
			</div>

			<div class="grid grid-cols-2 gap-4">
				<div>
					<label
						for="newsletter-fname"
						class="block text-sm font-medium text-gray-700">First Name</label>
					<input
						id="newsletter-fname"
						v-model="firstName"
						type="text"
						class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
				</div>
				<div>
					<label
						for="newsletter-lname"
						class="block text-sm font-medium text-gray-700">Last Name</label>
					<input
						id="newsletter-lname"
						v-model="lastName"
						type="text"
						class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
				</div>
			</div>

			<div>
				<label
					for="newsletter-company"
					class="block text-sm font-medium text-gray-700">Company</label>
				<input
					id="newsletter-company"
					v-model="company"
					type="text"
					class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
			</div>

			<fieldset>
				<legend class="block text-sm font-medium text-gray-700">Interests</legend>
				<div class="mt-2 space-y-2">
					<label class="flex items-center gap-2 text-sm text-gray-600">
						<input
							v-model="interestFileMaker"
							type="checkbox"
							class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
						FileMaker
					</label>
					<label class="flex items-center gap-2 text-sm text-gray-600">
						<input
							v-model="interestSalesforce"
							type="checkbox"
							class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
						Salesforce
					</label>
					<label class="flex items-center gap-2 text-sm text-gray-600">
						<input
							v-model="interestWebDevelopment"
							type="checkbox"
							class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
						Web Development
					</label>
				</div>
			</fieldset>

			<p
				v-if="error"
				class="text-sm text-red-500">Something went wrong subscribing you. Please try again.</p>

			<tw-button
				type="submit"
				class="btn-base btn-indigo mt-2 w-full"
				:loading="submitting"
				:disabled="submitting">
				Subscribe
			</tw-button>
		</form>
	</tw-modal>
</template>

<script lang="ts">
	export default { name: "NewsletterModal" } // name the component
</script>

<script lang="ts" setup>
	import {
		CheckCircleIcon
	} from "@heroicons/vue/24/outline"

	import {
		defineExpose,
		ref
	} from "vue"

	import {
		store as useUserStore
	} from "@/store/user"

	import api from "@/utilities/api"

	const userStore = useUserStore()

	const modal = ref<{open: boolean}>({open: false})
	const submitting = ref(false)
	const submitted = ref(false)
	const error = ref(false)
	const email = ref("")
	const firstName = ref("")
	const lastName = ref("")
	const company = ref("")
	const city = ref("")
	const state = ref("")
	const interestFileMaker = ref(false)
	const interestSalesforce = ref(false)
	const interestWebDevelopment = ref(false)

	// fill the form with whatever profile details we already have for the logged-in user
	function prefillFromUser() {
		const attributes = userStore?.user?.cognitoUser?.attributes || {}
		const address = JSON.parse(attributes.address || "{}")

		email.value = attributes.email || ""
		firstName.value = attributes.given_name || ""
		lastName.value = attributes.family_name || ""
		company.value = attributes["custom:company"] || ""
		city.value = address.locality || ""
		state.value = address.region || ""
	}

	// reveal the modal with a fresh, unsubmitted form
	function open() {
		submitting.value = false
		submitted.value = false
		error.value = false
		prefillFromUser()
		modal.value.open = true
	}

	// submits the form to the backend, which relays it to Mailchimp
	async function onSubmit(event: Event) {
		event.preventDefault()
		submitting.value = true
		error.value = false

		const interests = []

		if (interestFileMaker.value) {
			interests.push("fileMaker")
		}

		if (interestSalesforce.value) {
			interests.push("salesforce")
		}

		if (interestWebDevelopment.value) {
			interests.push("webDevelopment")
		}

		try {
			await api("post", "fmDataAPI", "/newsletter-subscribe", {
				email: email.value,
				firstName: firstName.value,
				lastName: lastName.value,
				company: company.value,
				city: city.value,
				state: state.value,
				interests
			})

			submitted.value = true
		}
		catch (e) {
			error.value = true
		}
		finally {
			submitting.value = false
		}
	}

	defineExpose({
		open
	})
</script>
